import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
  CheckCircle2, Copy, Server, Cpu, Loader2, PauseCircle, XCircle, ArrowRight,
} from "lucide-react";

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value && value !== 0) return null;
  return (
    <div className="grid grid-cols-12 gap-3 items-center">
      <div className="col-span-4 overline">{label}</div>
      <div className="col-span-7 font-mono text-sm text-zinc-100 break-all">{value}</div>
      <button
        onClick={() => {
          navigator.clipboard.writeText(String(value));
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }}
        className="col-span-1 inline-flex justify-end text-zinc-400 hover:text-signal"
        data-testid={`copy-${label.toLowerCase()}`}
      >
        <Copy size={14} />
        {copied && <span className="ml-1 text-[10px] uppercase tracking-widest">OK</span>}
      </button>
    </div>
  );
}

const STATUS = {
  active:    { Icon: CheckCircle2, color: "border-signal/60 text-signal",   label: "ACTIVE" },
  suspended: { Icon: PauseCircle,  color: "border-amber-700 text-amber-400", label: "SUSPENDED" },
  cancelled: { Icon: XCircle,      color: "border-red-900 text-red-400",     label: "CANCELLED" },
};

export default function SubscriptionDetail() {
  const { id } = useParams();
  const [sub, setSub] = useState(null);
  const [err, setErr] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);

  const load = () =>
    api
      .get(`/subscriptions/${id}`)
      .then((r) => setSub(r.data))
      .catch((e) => setErr(formatApiError(e)));

  useEffect(() => {
    load();
  }, [id]);

  if (err) {
    return (
      <div className="container-pad py-24 font-mono text-zinc-400" data-testid="sub-error">
        // {err}
      </div>
    );
  }
  if (!sub) {
    return (
      <div className="container-pad py-24 flex items-center gap-2 text-zinc-500">
        <Loader2 size={16} className="animate-spin" />
        <span className="font-mono text-xs uppercase tracking-widest">LOADING SUBSCRIPTION…</span>
      </div>
    );
  }

  const s = STATUS[sub.status] || STATUS.active;
  const isActive = sub.status === "active";
  const Icon = sub.service_subtype === "dedicated" ? Cpu : Server;

  const onCancel = async () => {
    if (!confirm(`Cancel ${sub.service_name} (${sub.plan_name})? The server will be released.`)) return;
    setCancelBusy(true);
    try {
      await api.post(`/subscriptions/${sub.id}/cancel`);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <div data-testid="subscription-detail-page" className="container-pad py-12 md:py-20 max-w-3xl">
      <div className={`card-flat p-8 ${s.color.split(" ")[0]}`}>
        <div className="flex items-center gap-3">
          <s.Icon size={22} className={s.color.split(" ")[1]} />
          <div className="overline">// SUBSCRIPTION {sub.id.slice(0, 8).toUpperCase()}</div>
        </div>

        <h1 className="mt-4 font-display text-3xl md:text-4xl tracking-tight flex items-center gap-3">
          <Icon size={28} className="text-signal" />
          {sub.service_name}
        </h1>
        <div className="mt-1 font-mono text-sm text-zinc-400">
          {sub.plan_name} · ${sub.daily_cost_usd.toFixed(2)} / day · ${sub.monthly_cost_usd.toFixed(2)} / mo
        </div>

        <div className="mt-6 grid grid-cols-12 gap-3 font-mono text-sm">
          <div className="col-span-4 overline">STATUS</div>
          <div className="col-span-8">
            <span className={`badge-mono ${s.color}`}>{s.label}</span>
          </div>
          <div className="col-span-4 overline">PROVISIONED</div>
          <div className="col-span-8 text-zinc-200">
            {sub.provisioned_at ? new Date(sub.provisioned_at).toLocaleString() : "—"}
          </div>
          <div className="col-span-4 overline">LAST BILLED</div>
          <div className="col-span-8 text-zinc-200">
            {sub.last_billed_at ? new Date(sub.last_billed_at).toLocaleString() : "—"}
          </div>
          <div className="col-span-4 overline">NEXT BILLING</div>
          <div className="col-span-8 text-zinc-200">
            {sub.next_billing_at ? new Date(sub.next_billing_at).toLocaleString() : "—"}
          </div>
        </div>

        {sub.delivery && isActive && (
          <div className="mt-8 border-t border-zinc-800 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Server size={16} className="text-signal" />
              <span className="font-mono text-xs tracking-widest text-white">SERVER CREDENTIALS</span>
            </div>
            <div className="space-y-3 card-flat p-5">
              <CopyField label="HOST" value={sub.delivery.server_host} />
              <CopyField label="PORT" value={sub.delivery.server_port} />
              <CopyField label="USERNAME" value={sub.delivery.server_username} />
              <CopyField label="PASSWORD" value={sub.delivery.server_password} />
              {sub.delivery.docs_url && <CopyField label="DOCS" value={sub.delivery.docs_url} />}
            </div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
              Store these securely. They will not be shown again in plain text.
            </div>
          </div>
        )}

        {sub.status === "suspended" && (
          <div className="mt-8 border border-amber-900/60 bg-amber-950/30 text-amber-300 text-sm p-4 rounded-sm font-mono">
            // Subscription suspended due to insufficient balance.
            <Link to="/topup" className="ml-2 underline">Top up to resume.</Link>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/dashboard" className="btn-ghost h-11 px-5 text-sm font-mono tracking-widest">
            BACK TO DASHBOARD
          </Link>
          {isActive && (
            <button
              onClick={onCancel}
              disabled={cancelBusy}
              data-testid="cancel-subscription"
              className="btn-ghost h-11 px-5 text-sm font-mono tracking-widest border-red-900/60 text-red-300 hover:border-red-700 disabled:opacity-50"
            >
              {cancelBusy ? "CANCELLING…" : "CANCEL SUBSCRIPTION"}
            </button>
          )}
          <Link to="/catalog" className="btn-signal h-11 px-5 text-sm font-mono tracking-widest">
            DEPLOY ANOTHER <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
